import React, { useEffect, useRef, useState } from "react";
import { Container, Grid, Typography, Box } from "@mui/material";
import GroupsIcon from "@mui/icons-material/Groups";
import CastForEducationIcon from "@mui/icons-material/CastForEducation";
import WorkHistoryIcon from "@mui/icons-material/WorkHistory";
import TerminalIcon from "@mui/icons-material/Terminal";
import SchoolIcon from "@mui/icons-material/School";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import { brand } from "../theme";

const stats = [
  { icon: <CalendarMonthIcon />, value: 2018, suffix: "", label: "Founded · MSME Registered", plain: true },
  { icon: <GroupsIcon />, value: 5000, suffix: "+", label: "Students Trained & Accelerated" },
  { icon: <CastForEducationIcon />, value: 20, suffix: "+", label: "Institutional Masterclasses" },
  { icon: <WorkHistoryIcon />, value: 25, suffix: "+", label: "Experiential Internships Run" },
  { icon: <SchoolIcon />, value: 5, suffix: "", label: "Senior Technical Instructors" },
  { icon: <TerminalIcon />, value: 8, suffix: "", label: "Commercial Software Developers" },
];

const useCountUp = (end, duration = 1600, start = false) => {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!start) return;
    let raf;
    let startTime;
    const step = (ts) => {
      if (!startTime) startTime = ts;
      const progress = Math.min((ts - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(Math.floor(eased * end));
      if (progress < 1) raf = requestAnimationFrame(step);
      else setValue(end);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [end, duration, start]);
  return value;
};

const StatItem = ({ stat, start }) => {
  // Founded year shouldn't animate as a count; render plainly.
  const animated = useCountUp(stat.plain ? 0 : stat.value, 1600, start && !stat.plain);
  const display = stat.plain ? stat.value : animated;
  return (
    <Box sx={{ textAlign: "center", px: 1 }}>
      <Box
        sx={{
          width: 60,
          height: 60,
          mx: "auto",
          mb: 1.5,
          borderRadius: "16px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#fff",
          background: "rgba(255,255,255,0.16)",
          "& svg": { fontSize: 30 },
        }}
      >
        {stat.icon}
      </Box>
      <Typography sx={{ fontWeight: 800, fontSize: { xs: "1.8rem", md: "2.4rem" }, color: "#fff", lineHeight: 1 }}>
        {display}
        {stat.suffix}
      </Typography>
      <Typography sx={{ color: "rgba(255,255,255,0.85)", fontSize: "0.92rem", mt: 1 }}>
        {stat.label}
      </Typography>
    </Box>
  );
};

const Stats = () => {
  const ref = useRef(null);
  const [start, setStart] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    if (!("IntersectionObserver" in window)) {
      setStart(true);
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setStart(true);
          observer.disconnect();
        }
      },
      { threshold: 0.3 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <Box
      ref={ref}
      sx={{
        background: `linear-gradient(135deg, ${brand.blueDark} 0%, ${brand.blue} 100%)`,
        py: { xs: 6, md: 8 },
        position: "relative",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: -80,
          right: -60,
          width: 240,
          height: 240,
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(252,122,70,0.25), transparent 70%)",
        }}
      />
      <Container sx={{ position: "relative", zIndex: 1 }}>
        <Typography
          align="center"
          sx={{ color: "rgba(255,255,255,0.9)", fontWeight: 700, letterSpacing: "0.12em", mb: 0.5, fontSize: "0.8rem" }}
        >
          COMPANY SNAPSHOT
        </Typography>
        <Typography
          variant="h4"
          align="center"
          sx={{ color: "#fff", fontWeight: 800, mb: { xs: 4, md: 6 }, fontSize: { xs: "1.6rem", md: "2.1rem" } }}
        >
          A Verified Operational Baseline
        </Typography>
        <Grid container spacing={{ xs: 3, md: 4 }}>
          {stats.map((stat, i) => (
            <Grid item xs={6} sm={4} md={2} key={i}>
              <StatItem stat={stat} start={start} />
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default Stats;
